import React from 'react';
import { Form, Input, Select, Button, Typography, Tooltip, Card, Slider, DatePicker, InputNumber } from 'antd';
import { MinusCircleOutlined, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { decisionMakers, countries, industries, company_sizes } from './CompanyTargetOptions';

const { TextArea } = Input;
const { Title } = Typography;

const CompanyTargetForm = ({ form }) => {
  const countries_options = countries.map(item => ({ value: item }));
  const industries_options = industries.map(item => ({ value: item }));
  const company_size_options = company_sizes.map(item => ({ value: item }));
  const filterOption = (input, option) =>
    (option?.value ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <>
      <Form.Item
        name="name"
        label="Campaign name"
        rules={[{ required: true, message: "Name this campaign" }]}
      >
        <TextArea autoSize placeholder="" />
      </Form.Item>    
      <Form.Item
        name="productDescription"
        label="Product Description"
        rules={[{ required: true, message: "Please provide a description of the product" }]}
      >
        <TextArea placeholder="Describe the product offering" />
      </Form.Item>
      <Form.Item
        name="customerPainPoints"
        label="Customer Pain Points"
        rules={[{ required: true, message: "Please describe the customer's pain points" }]}
      >
        <TextArea placeholder="Describe your customer's pain points" />
      </Form.Item>
      <Form.Item
        name="customerValue"
        label="Customer Value"
        rules={[{ required: true, message: "Please describe the value your product provides" }]}
      >
        <TextArea placeholder="Describe the value proposition" />
      </Form.Item>

      <Form.Item
        label="Target Company's Size"
        rules={[{ required: true, message: "Please provide your target company's size" }]}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 8 }}>From</span>
          <Form.Item
            name="minSize"
            rules={[{ required: true, message: 'Please input the minimum size!' }]}
            style={{ margin: 0, marginRight: 16 }}
            validateStatus={form.getFieldError('minSize').length ? 'error' : ''}
            help=""
          >
            <InputNumber min={1} placeholder="Min" style={{ width: '100%' }} />
          </Form.Item>
          <span style={{ marginRight: 8 }}>to</span>
          <Form.Item
            name="maxSize"
            rules={[{ required: true, message: 'Please input the maximum size!' }]}
            style={{ margin: 0 }}
            validateStatus={form.getFieldError('maxSize').length ? 'error' : ''}
            help=""
          >
            <InputNumber min={1} placeholder="Max" style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <Form.ErrorList errors={form.getFieldError('minSize').concat(form.getFieldError('maxSize'))} />
      </Form.Item>


      <Form.Item
        name="location"
        label="Location"
        rules={[{ required: true, message: "Please provide the location of your target company" }]}
      >
        <Select
          mode="multiple"
          placeholder="Select location"
          allowClear
          optionFilterProp="children"
          filterOption={filterOption}
          options={countries_options}
        />
      </Form.Item>
      <Form.Item
        name="industry"
        label="Industry"
        rules={[{ required: true, message: "Please provide the industry of your target company" }]}
      >
        <Select
          placeholder="Select industry"
          mode="multiple"
          allowClear
          filterOption={filterOption}
          options={industries_options}
        />
      </Form.Item>

      <Form.Item
        name="needFunding"
        label="Does the company need to be funded?"
        rules={[{ required: true, message: "Please select if the company needs to be funded" }]}
      >
        <Select placeholder="Select an option">
          <Select.Option value="yes">Yes</Select.Option>
          <Select.Option value="no">No</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        shouldUpdate={(prevValues, currentValues) => prevValues.needFunding !== currentValues.needFunding}
        noStyle
      >
        {({ getFieldValue }) =>
          getFieldValue('needFunding') === 'yes' ? (
            <>
              <Form.Item
                name="lastFundingDate"
                label={
                  <>
                    Last Funding Date Greater than 
                    <Tooltip title="If set, we will filter companies that have been funded later than the date set.">
                      <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                    </Tooltip>
                  </>
                }
              >
                <DatePicker />
              </Form.Item>
            </>
          ) : null
        }
      </Form.Item>

      <Form.List name="keyQuestions" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <Card key={field.key} style={{ marginBottom: 16 }}>
                <Form.Item
                  {...field}
                  name={[field.name, 'question']}
                  fieldKey={[field.fieldKey, 'question']}
                  label={`Key Question ${index + 1}`}
                  rules={[{ required: index === 0, message: "Please provide the key question" }]}
                >
                  <Input placeholder="E.g Does this company hire a sales person" />
                </Form.Item>
                <Form.Item
                  {...field}
                  name={[field.name, 'research_instruction']}
                  fieldKey={[field.fieldKey, 'research_instruction']}
                  label="Research Instruction (Optional)"
                >
                  <TextArea placeholder="Check out their linkedin page and see if there is any sales related role" />
                </Form.Item>
                {fields.length > 1 && (
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                )}
              </Card>
            ))}
            {fields.length < 5 && (
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Key Question
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>

      <Title level={4}>Contact Person Information</Title>
      <Form.Item
        name="contactPreference"
        label="Contact Preference"
        rules={[{ required: true, message: "Please provide who you wish to connect" }]}
      >
        <Select
          mode="tags"
          placeholder="Select contact preference"
          options={decisionMakers}
        />
      </Form.Item>
      <Form.List name="contactKeyQuestions" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <Card key={field.key} style={{ marginBottom: 16 }}>
                <Form.Item
                  {...field}
                  name={[field.name, 'question']}
                  fieldKey={[field.fieldKey, 'question']}
                  label={`Key Question ${index + 1}`}
                  rules={[{ required: index === 0, message: "Please provide the key question" }]}
                >
                  <Input placeholder="Enter key question" />
                </Form.Item>
                {fields.length > 1 && (
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                )}
              </Card>
            ))}
            {fields.length < 5 && (
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Key Question
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>
    </>
  );
};

export default CompanyTargetForm;
